/**
 * Weekly Firm Performance Report
 * Directors/partners view aggregating deadlines, filing compliance, and time billed.
 */
import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Send, Clock, FileCheck, AlertTriangle, CheckCircle2, TrendingUp, RefreshCw } from "lucide-react";

import { API } from '../../config/api';

export default function WeeklyReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showHtml, setShowHtml] = useState(false);

  const userId = localStorage.getItem("bonsai_user_id");

  useEffect(() => {
    if (!userId) { window.location.href = "/login"; }
  }, [userId]);

  async function fetchReport() {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/bonsai/emails/reports/weekly-performance`, {
        method: "POST",
        headers: { "X-Bonsai-User-Id": userId },
      });
      const d = await resp.json();
      setData(d);
    } catch (e) {
      console.error("Failed to generate report:", e);
    }
    setLoading(false);
  }

  async function sendEmail() {
    setSending(true);
    try {
      const resp = await fetch(`${API}/bonsai/emails/reports/weekly-performance?send_email=true`, {
        method: "POST",
        headers: { "X-Bonsai-User-Id": userId },
      });
      const d = await resp.json();
      if (d.sent) alert("Report sent to your inbox!");
      else alert("Report composed but email send requires Microsoft auth in production.");
    } catch (e) {
      alert("Failed to send: " + e.message);
    }
    setSending(false);
  }

  useEffect(() => { fetchReport(); }, []);

  const report = data?.report || {};
  const deadlines = report.deadlines || {};
  const filing = report.filing || {};
  const time = report.time_billed || {};

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="weekly-report-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold" data-testid="report-title">Weekly Firm Performance</h1>
              <p className="text-sm text-gray-400">
                {report.period ? `${report.period.from} — ${report.period.to}` : "Last 7 days"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading} className="border-gray-700 text-gray-300" data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" onClick={sendEmail} disabled={sending} className="bg-indigo-600 hover:bg-indigo-500" data-testid="send-email-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> {sending ? "Sending..." : "Email to me"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-12">Generating report...</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4 text-center">
                  <CheckCircle2 className="w-5 h-5 text-status-green mx-auto mb-1" />
                  <div className="text-3xl font-bold text-white" data-testid="kpi-completed">{deadlines.completed_this_week || 0}</div>
                  <div className="text-xs text-gray-400">Deadlines completed</div>
                </CardContent>
              </Card>
              <Card className={`bg-gray-900 ${(deadlines.overdue || 0) > 0 ? "border-red-900/50" : "border-gray-800"}`}>
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-5 h-5 text-status-red mx-auto mb-1" />
                  <div className={`text-3xl font-bold ${(deadlines.overdue || 0) > 0 ? "text-status-red" : "text-status-green"}`} data-testid="kpi-overdue">{deadlines.overdue || 0}</div>
                  <div className="text-xs text-gray-400">Overdue</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4 text-center">
                  <FileCheck className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <div className="text-3xl font-bold text-blue-400" data-testid="kpi-filed">{filing.total_filed || 0}</div>
                  <div className="text-xs text-gray-400">Emails filed</div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4 text-center">
                  <Clock className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                  <div className="text-3xl font-bold text-purple-400" data-testid="kpi-hours">{time.total_hours || 0}h</div>
                  <div className="text-xs text-gray-400">Hours billed</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* Deadlines by Fee Earner */}
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold text-gray-300">Deadlines by Fee Earner</CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-2">
                  {(deadlines.by_user || []).length === 0 ? (
                    <div className="px-4 py-3 text-xs text-gray-500">No open deadlines</div>
                  ) : (
                    <table className="w-full text-xs" data-testid="deadline-user-table">
                      <thead><tr className="text-gray-500 border-b border-gray-800"><th className="px-4 py-1 text-left">Name</th><th className="px-4 py-1 text-center">Open</th><th className="px-4 py-1 text-left">Status</th></tr></thead>
                      <tbody>
                        {(deadlines.by_user || []).map((u, i) => (
                          <tr key={i} className="border-b border-gray-800/40">
                            <td className="px-4 py-2 text-white font-medium">{u.name}</td>
                            <td className="px-4 py-2 text-center font-bold text-white">{u.total}</td>
                            <td className="px-4 py-2">
                              {u.overdue > 0 && <Badge variant="outline" className="text-status-red border-red-700 text-[10px] mr-1">{u.overdue} overdue</Badge>}
                              {u.high_risk > 0 && <Badge variant="outline" className="text-status-amber border-orange-700 text-[10px]">{u.high_risk} risk</Badge>}
                              {u.overdue === 0 && u.high_risk === 0 && <span className="text-status-green">all on track</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              {/* Filing Compliance */}
              <Card className="bg-gray-900 border-gray-800">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold text-gray-300">Filing Compliance</CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-2">
                  {(filing.by_user || []).length === 0 ? (
                    <div className="px-4 py-3 text-xs text-gray-500">No filing data</div>
                  ) : (
                    <table className="w-full text-xs" data-testid="filing-user-table">
                      <thead><tr className="text-gray-500 border-b border-gray-800"><th className="px-4 py-1 text-left">Fee Earner</th><th className="px-4 py-1 text-center">Filed</th><th className="px-4 py-1 text-left">Activity</th></tr></thead>
                      <tbody>
                        {(filing.by_user || []).map((r, i) => (
                          <tr key={i} className="border-b border-gray-800/40">
                            <td className="px-4 py-2 text-white"><span className="font-bold text-gray-400 mr-1">{r.initials}</span>{r.name}</td>
                            <td className={`px-4 py-2 text-center font-bold ${r.filed > 0 ? "text-status-green" : "text-gray-500"}`}>{r.filed}</td>
                            <td className="px-4 py-2">
                              <div className="w-20 h-1.5 bg-gray-800 rounded-md overflow-hidden">
                                <div className="h-full rounded-md bg-status-green transition-all" style={{ width: `${Math.round((r.filed / Math.max(...(filing.by_user || []).map(x => x.filed), 1)) * 100)}%` }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Time Billed */}
            <Card className="bg-gray-900 border-gray-800 mb-6">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-gray-300">Time Billed via LawBillity</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-3xl font-bold text-purple-400">{time.total_hours || 0}h</span>
                  <span className="text-sm text-gray-400">across {time.total_entries || 0} entries</span>
                </div>
                {(time.by_user || []).length > 0 && (
                  <div className="space-y-2">
                    {(time.by_user || []).map((u, i) => (
                      <div key={i} className="flex items-center justify-between py-1 px-2 bg-gray-800/30 rounded">
                        <span className="text-xs text-gray-300">{u.email}</span>
                        <span className="text-xs font-bold text-purple-300">{u.hours}h ({u.entries} entries)</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview / Send */}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => setShowHtml(!showHtml)} className="border-gray-700 text-gray-400" data-testid="preview-html-btn">
                {showHtml ? "Hide" : "Preview"} email HTML
              </Button>
            </div>
            {showHtml && data?.email_html && (
              <Card className="bg-white mt-3 border-gray-300">
                <CardContent className="p-0">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.email_html) }} />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
